import { useState } from "react";
import { toast } from "react-hot-toast"
import { Check, Upload, Wallet, PartyPopper, X } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { useNavigate } from "react-router-dom";

const paymentMethods = [
  {
    id: "meezan",
    name: "Meezan Bank",
    logo: "/placeholder.svg?height=40&width=40",
    accountName: "Muhammad Khawar Hayyat",
    accountNumber: "0302-6589335",
    currency: "PKR",
    amount: 1000,
  },
  {
    id: "jazzcash",
    name: "Jazz Cash",
    logo: "/placeholder.svg?height=40&width=40",
    accountName: "Muhammad Khawar Hayyat",
    accountNumber: "0302-6589335",
    currency: "PKR",
    amount: 1000,
  },
  {
    id: "easypaisa",
    name: "EasyPaisa",
    logo: "/placeholder.svg?height=40&width=40",
    accountName: "Muhammad Khawar Hayyat",
    accountNumber: "0302-6589335",
    currency: "PKR",
    amount: 1000,
  },
  {
    id: "nayapay",
    name: "NayaPay",
    logo: "/placeholder.svg?height=40&width=40",
    accountName: "Muhammad Khawar Hayyat",
    accountNumber: "0302-6589335",
    currency: "PKR",
    amount: 1000,
  },
  {
    id: "sadapay",
    name: "SadaPay",
    logo: "/placeholder.svg?height=40&width=40",
    accountName: "Muhammad Khawar Hayyat",
    accountNumber: "0302-6589335",
    currency: "PKR",
    amount: 1000,
  },
];

const PaymentCheckout = ({ step, onNext, onSelectMethod, selectedMethod, onFileUpload, isSubmitting }) => {
  const [receipt, setReceipt] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        toast.error("File size should not exceed 5MB")
        return
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file")
        return
      }

      setReceipt(file)
      setPreviewUrl(URL.createObjectURL(file))
      onFileUpload(file)
    }
  }

  const handleRemoveReceipt = () => {
    setReceipt(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
    onFileUpload(null)
  }

  const renderStepContent = () => {
    switch (step) {
      case "select":
        return (
          <Card>
            <CardContent className="p-6">
              <h2 className="mb-4 text-xl font-semibold">Choose Payment Method</h2>
              <RadioGroup 
                value={selectedMethod} 
                onValueChange={(value) => onSelectMethod(value)} 
                className="grid gap-4 md:grid-cols-3"
              >
                {paymentMethods.map((method) => (
                  <Label key={method.id} htmlFor={method.id} className="cursor-pointer">
                    <RadioGroupItem value={method.id} id={method.id} className="peer sr-only" />
                    <Card className={`border-2 transition-all ${selectedMethod === method.id ? 'border-[#F5A623] bg-[#F5A623]/5 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}>
                      <CardContent className="flex flex-col items-center space-y-3 p-6">
                        <div className={`rounded-full p-2 ${selectedMethod === method.id ? 'bg-[#F5A623]/20' : 'bg-slate-100'}`}>
                          <Wallet className={`h-6 w-6 ${selectedMethod === method.id ? 'text-[#F5A623]' : 'text-slate-600'}`} />
                        </div>
                        <div className="text-center">
                          <h3 className={`font-medium ${selectedMethod === method.id ? 'text-[#F5A623]' : ''}`}>{method.name}</h3>
                          <p className="text-sm text-muted-foreground">Mobile Wallet</p>
                        </div>
                        {selectedMethod === method.id && (
                          <div className="flex items-center gap-1 text-[#F5A623] text-sm font-medium">
                            <Check className="h-4 w-4" />
                            Selected
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </Label>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
        )
      case "details":
        const method = paymentMethods.find((m) => m.id === selectedMethod)
        return (
          <>
            <Card>
              <CardContent className="p-6">
                <h2 className="mb-6 text-xl font-semibold">Account Details</h2>
                <div className="grid gap-6">
                  <div className="flex items-center justify-between rounded-lg bg-slate-100 p-4">
                    <div className="flex items-center space-x-4">
                      <div className="rounded-full bg-white p-2">
                        <Wallet className="h-6 w-6 text-slate-600" />
                      </div>
                      <div>
                        <h3 className="font-medium">{method.name}</h3>
                        <p className="text-sm text-muted-foreground">Selected Payment Method</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4">
                    <div>
                      <Label>Account Name</Label>
                      <Input value={method.accountName} readOnly className="bg-slate-100" />
                    </div>
                    <div>
                      <Label>Account Number</Label>
                      <Input value={method.accountNumber} readOnly className="bg-slate-100" />
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label>Currency</Label>
                        <Input value={method.currency} readOnly className="bg-slate-100" />
                      </div>
                      <div>
                        <Label>Amount</Label>
                        <Input value={`${method.amount.toLocaleString()}`} readOnly className="bg-slate-100" />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            {
              !receipt && (
              <Card className="mt-6">
                <CardContent className="p-6">
                  <h3 className="mb-4 text-lg font-medium">Payment Process Overview</h3>
                  <div className="space-y-4">
                    <div className="rounded-lg bg-primary/5 p-4">
                      <div className="grid gap-3">
                        {/* Step 1 */}
                        <div className="flex items-start space-x-3">
                          <div className="rounded-full bg-primary/10 p-1">
                            <Check className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">Step 1: Make the Payment</p>
                            <p className="text-sm text-muted-foreground">
                              Transfer {method.amount} {method.currency} to the provided {method.accountName} account
                            </p>
                          </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex items-start space-x-3">
                          <div className="rounded-full bg-primary/10 p-1">
                            <Check className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">Step 2: Save the Receipt</p>
                            <p className="text-sm text-muted-foreground">
                              Take a screenshot or save the payment confirmation
                            </p>
                          </div>
                        </div>

                        {/* Step 3 */}
                        <div className="flex items-start space-x-3">
                          <div className="rounded-full bg-primary/10 p-1">
                            <Check className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">Step 3: Upload Proof</p>
                            <p className="text-sm text-muted-foreground">Upload the payment receipt in the next step</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tip Section */}
                    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                      <p className="text-sm text-muted-foreground">
                        💡 <span className="font-medium">Tip:</span> Make sure to keep your transaction ID or reference number handy
                        for faster verification.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )
        case "upload":
          return (
            <Card>
              <CardContent className="p-6">
                <h2 className="mb-6 text-xl font-semibold">Upload Receipt</h2>
                <div className="grid gap-6">
                  {!receipt ? (
                    <div className="rounded-lg border-2 border-dashed p-8">
                      <div className="flex flex-col items-center space-y-4">
                        <div className="rounded-full bg-[#F5A623]/10 p-3">
                          <Upload className="h-6 w-6 text-[#F5A623]" />
                        </div>
                        <div className="text-center">
                          <h3 className="font-medium">Upload Payment Receipt</h3>
                          <p className="text-sm text-muted-foreground">
                            Please upload a screenshot of your payment confirmation
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">(Max size: 5MB, Image files only)</p>
                        </div>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="max-w-xs"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium">Receipt Preview</h3>
                        <Button variant="ghost" size="sm" onClick={handleRemoveReceipt} disabled={isSubmitting}>
                          <X className="h-4 w-4 mr-2" />
                          Remove
                        </Button>
                      </div>
                      {/* Fixed: Receipt preview should not be cropped */}
                      <div className="relative w-full rounded-md overflow-hidden bg-gray-100">
                        {previewUrl && (
                          <img
                            src={previewUrl || "/placeholder.svg"}
                            alt="Receipt preview"
                            className="w-full h-auto object-contain max-h-[600px]"
                          />
                        )}
                      </div>
                    </div>
                  )}
        
                  {/* Features Section */}
                  <div className="rounded-lg bg-gradient-to-br from-[#F5A623]/5 via-[#F5A623]/10 to-[#F5A623]/5 p-6">
                    <h3 className="mb-4 text-lg font-medium">What you'll get</h3>
                    <ul className="grid gap-3 text-sm">
                      <li className="flex items-center space-x-2">
                        <Check className="h-4 w-4 text-[#F5A623]" />
                        <span>Access to Trending Dashboard</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Check className="h-4 w-4 text-[#F5A623]" />
                        <span>Join Exclusive Training Sessions</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Check className="h-4 w-4 text-[#F5A623]" />
                        <span>Maximum Chances of Being Hired</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
      case "success":
        return (
          <Card>
            <CardContent className="p-8">
              <div className="flex flex-col items-center space-y-6 text-center">
                <div className="rounded-full bg-primary/10 p-4">
                  <PartyPopper className="h-12 w-12 text-primary" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-semibold">Payment Successful!</h2>
                  <p className="text-muted-foreground">
                    Thank you for your payment. Your account will be activated shortly.
                  </p>
                </div>

                <div className="w-full max-w-sm rounded-lg bg-primary/5 p-6">
                  <div className="space-y-4">
                    <h3 className="font-medium">What happens next?</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center space-x-3">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Our team will verify your payment</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Check className="h-4 w-4 text-primary" />
                        <span>You'll receive a confirmation email</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Your dashboard access will be activated</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <Button className="bg-[#ff9906] text-white cursor-pointer hover:bg-[#efb057]" 
                     onClick={() => navigate("/dashboard")}
                  >
                    Click to go to dashboard
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      default:
        return null
    }
  }

  return renderStepContent()
}

export default PaymentCheckout;